import React, { useState, useEffect } from "react";

// This shell is built with fixed px inline styles (see note below on why
// inline styles), so it has no way to respond to viewport width the way a
// stylesheet with @media queries would. This hook is the substitute: read
// the viewport width, branch the affected inline style values in each
// component below. Called independently in every component that needs it
// (rather than computed once and threaded down as a prop) so each piece
// stays self-contained — StatusPanel in particular is also rendered on its
// own, outside the default EditorialForm tree, and needs its own read.
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false,
  );
  useEffect(() => {
    const onResize = () =>
      setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/* ============================================================================
   EditorialForm — the design, as real React with inline styles.

   Why inline styles: a stylesheet has to win specificity fights against the
   app's existing CSS and against inline styles already in the JSX. Inline
   styles here cannot lose. This is why the CSS-only attempts kept drifting.

   This component is PRESENTATION ONLY. It owns no state and calls no
   contracts — every value and handler comes in as a prop, so it drops into
   TabbedOperations without touching any logic.
   ========================================================================== */

const SERIF = "'Instrument Serif', Georgia, serif";
const MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";
const SANS = "'Space Grotesk', -apple-system, sans-serif";

const INK = "#16181A";
const INK_MID = "#4A4A46";
const INK_SOFT = "#8E8E88";
const INK_FAINT = "#A5A5A0";
const GHOST = "#C2C2BC";
const RULE = "#E6E6E1";
const RULE_SOFT = "#F2F2EE";
const ACCENT = "#56AEF0";
const ACCENT_INK = "#08222F";
const LINK = "#3E8FCC";
const DANGER = "#C4462E";

const TABS = [
  { id: "transfer", label: "Transfer" },
  { id: "deposit", label: "Deposit" },
  { id: "withdraw", label: "Withdraw" },
  { id: "history", label: "History" },
];

function Tabs({ activeTab, onTab }) {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        borderBottom: "1px solid #ECECE7",
        marginBottom: isMobile ? 20 : 30,
      }}
    >
      {TABS.map((t) => {
        const on = activeTab === t.id;
        return (
          <div
            key={t.id}
            onClick={() => onTab(t.id)}
            style={{
              minWidth: 0, // lets the grid column shrink below the label's
              // natural width instead of blowing out the shell's width
              marginBottom: -1,
              padding: isMobile ? "10px 2px 12px" : "12px 0 14px",
              textAlign: "center",
              cursor: "pointer",
              fontFamily: SERIF,
              fontSize: isMobile ? 16 : 22,
              fontStyle: on ? "italic" : "normal",
              color: on ? INK : INK_FAINT,
              borderBottom: `1.5px solid ${on ? INK : "transparent"}`,
              transition: "color .15s",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t.label}
          </div>
        );
      })}
    </div>
  );
}

function Caret() {
  return (
    <span
      style={{
        width: 0,
        height: 0,
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        borderTop: "4.5px solid #9A9A94",
      }}
    />
  );
}

/* ---------------------------------------------------------------- amount card */

function AmountCard({
  netLabel,
  onOpenNetwork,
  tokenIcon,
  chainIcon,
  balanceLabel,
  amount,
  onAmount,
  onPercent,
  percentEnabled,
  isOver,
}) {
  const isMobile = useIsMobile();
  const has = amount !== "" && amount != null;
  const numColor = isOver ? DANGER : has ? INK : GHOST;
  const chars = Math.max(1, String(amount || "0").length);
  const numeralSize = isMobile ? 46 : 62;
  // Mild upward nudge so the serif input doesn't sit on the percent chips
  // (Chrome's input metrics make it taller than line-height alone). Kept
  // smaller than before so the amount doesn't collide with the selector
  // above — top/bottom padding on the amount zone owns that gap instead.
  const numeralTop = isMobile ? -3 : -4;

  return (
    <div
      style={{
        position: "relative",
        // Taller than the old 168/184 so the amount zone can keep clear
        // air above (selector) and below (percent chips) at every width.
        height: isMobile ? 196 : 220,
        boxSizing: "border-box",
        padding: isMobile ? "16px 18px 18px" : "20px 24px 24px",
        background: "#FFFFFF",
        border: `1px solid ${RULE}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flex: "none",
        }}
      >
        <div
          onClick={onOpenNetwork}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 12px",
            border: `1px solid ${RULE}`,
            cursor: "pointer",
            fontFamily: MONO,
            fontSize: 10.5,
            letterSpacing: "0.14em",
            textTransform: "none",
            color: INK_MID,
            whiteSpace: "nowrap",
          }}
        >
          {tokenIcon && (
            // Token icon with the chain as a small overlapping badge —
            // same pairing MobileShell's network pill uses on mobile, kept
            // identical here so the two platforms read as one product.
            <span style={{ position: "relative", width: 18, height: 18, flex: "none" }}>
              <img
                src={tokenIcon}
                alt=""
                style={{ width: 18, height: 18, borderRadius: "50%", display: "block" }}
              />
              {chainIcon && (
                <img
                  src={chainIcon}
                  alt=""
                  style={{
                    position: "absolute",
                    right: -3,
                    bottom: -3,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    border: "1.5px solid #FFFFFF",
                    display: "block",
                  }}
                />
              )}
            </span>
          )}
          <span>{netLabel}</span>
          <Caret />
        </div>

        {balanceLabel ? (
          <div
            style={{
              fontFamily: MONO,
              fontSize: 10.5,
              letterSpacing: "0.12em",
              textTransform: "none",
              color: INK_SOFT,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {balanceLabel}
          </div>
        ) : null}
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          // Guaranteed clearance from the chain/token selector above and
          // the percent chips below — independent of how flex centers the
          // numeral inside the leftover height.
          paddingTop: isMobile ? 14 : 18,
          paddingBottom: isMobile ? 10 : 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 2, maxWidth: "100%" }}>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: isMobile ? 32 : 44,
              color: numColor,
              // Moves with the numeral below (position+top, same value) so
              // the two stay baseline-aligned with each other — shifting
              // only the numeral up would leave "$" sitting noticeably
              // lower/misaligned relative to it.
              position: "relative",
              top: numeralTop,
            }}
          >
            $
          </div>
          <input
            value={amount}
            onChange={(e) => onAmount(e.target.value.replace(/[^0-9.]/g, ""))}
            placeholder="0"
            inputMode="decimal"
            style={{
              width: `${chars}ch`,
              minWidth: "1ch",
              maxWidth: "100%",
              padding: 0,
              border: 0,
              outline: "none",
              background: "transparent",
              fontFamily: SERIF,
              fontSize: numeralSize,
              lineHeight: 1,
              // Chrome renders this <input> taller than lineHeight:1
              // implies — Instrument Serif's own ascent/descent metrics
              // push a single-line <input>'s intrinsic height well past
              // what line-height suggests for this element type (a plain
              // <div> doesn't have this problem). The excess was
              // overlapping the percent chips row below. Not a style
              // choice — a cross-browser font-metric quirk with no CSS-only
              // fix available on an unstyled/no-className input, so it's
              // fixed here directly rather than by a prop.
              height: numeralSize,
              // Even at the corrected height, baseline-aligning this
              // numeral against the "$" (different font sizes put their
              // baseline at different fractions of their own box) leaves
              // the numeral's box ending a couple px past where the
              // baseline math centers the row, just enough to still touch
              // the chips below. margin-bottom can't fix this — margin is
              // outside the border-box, so it never shows up in the
              // element's own rect, only in the gap after it (irrelevant
              // here, nothing follows). position+top actually repaints the
              // box higher.
              position: "relative",
              top: numeralTop,
              letterSpacing: "-0.01em",
              color: numColor,
              textAlign: "left",
            }}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: isMobile ? 6 : 8, justifyContent: "center", flex: "none" }}>
        {["20%", "50%", "75%", "Max"].map((p) => (
          <div
            key={p}
            onClick={() => percentEnabled && onPercent(p === "Max" ? "max" : parseInt(p, 10))}
            style={{
              padding: isMobile ? "6px 10px" : "7px 14px",
              border: "1px solid #EAEAE5",
              fontFamily: MONO,
              fontSize: 11,
              color: percentEnabled ? INK_MID : "#BDBDB7",
              cursor: percentEnabled ? "pointer" : "default",
            }}
          >
            {p}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- receiver row */

function ReceiverRow({ value, onChange, demoAddress, error }) {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        height: isMobile ? 56 : 62,
        boxSizing: "border-box",
        padding: isMobile ? "0 16px" : "0 24px",
        background: "#FFFFFF",
        // No dedicated error-text slot exists in this shell for the
        // receiver row (only the amount row has one, further down) — a
        // reddened border is the least invasive way to surface an invalid
        // address without adding new layout.
        border: `1px solid ${error ? DANGER : RULE}`,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter receiver address"
        style={{
          flex: 1,
          minWidth: 0,
          border: 0,
          outline: "none",
          background: "transparent",
          padding: 0,
          fontFamily: MONO,
          fontSize: isMobile ? 11.5 : 12.5,
          letterSpacing: "0.02em",
          color: error ? DANGER : INK,
        }}
      />
      {!value && demoAddress && (
        <div
          onClick={() => onChange(demoAddress)}
          style={{
            flex: "none",
            whiteSpace: "nowrap",
            fontFamily: MONO,
            fontSize: isMobile ? 10 : 11,
            letterSpacing: "0.1em",
            textTransform: "none",
            color: LINK,
            cursor: "pointer",
          }}
        >
          Use demo account
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- CTA */

function Cta({ label, enabled, onClick }) {
  const isMobile = useIsMobile();
  return (
    <div
      onClick={() => enabled && onClick()}
      style={{
        height: isMobile ? 56 : 66,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 999,
        fontFamily: SANS,
        fontSize: isMobile ? 15 : 16,
        fontWeight: 600,
        letterSpacing: "0.01em",
        background: enabled ? ACCENT : "#F1F1ED",
        color: enabled ? ACCENT_INK : "#AFAFA9",
        cursor: enabled ? "pointer" : "default",
        transition: "background .15s",
      }}
    >
      {label}
    </div>
  );
}

/* ------------------------------------------------------------ status panel */

export function StatusPanel({
  pending,
  title,
  meta,
  onReceipt,
  onReset,
  resetLabel = "Make transfer",
}) {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        padding: isMobile ? "32px 20px" : "44px 32px",
        background: "#FFFFFF",
        border: `1px solid ${RULE}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          background: pending ? "#F6F6F3" : "#EAF6FE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {pending ? (
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              border: "1.6px solid #D8E9F7",
              borderTopColor: LINK,
              display: "inline-block",
              animation: "st-spin .8s linear infinite",
            }}
          />
        ) : (
          <span
            style={{
              width: 18,
              height: 10,
              marginTop: -5,
              borderLeft: "1.8px solid #2F87C4",
              borderBottom: "1.8px solid #2F87C4",
              transform: "rotate(-45deg)",
              display: "inline-block",
            }}
          />
        )}
      </div>

      <div style={{ fontFamily: SERIF, fontSize: isMobile ? 24 : 30, textAlign: "center" }}>{title}</div>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 11.5,
          lineHeight: 1.8,
          color: "#7E7E79",
          textAlign: "center",
        }}
      >
        {meta}
      </div>

      {!pending && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 6,
            }}
          >
            {onReceipt && (
              <div
                onClick={onReceipt}
                style={{
                  fontFamily: SANS,
                  fontSize: 14,
                  fontWeight: 500,
                  padding: isMobile ? "12px 22px" : "13px 26px",
                  background: INK,
                  color: "#FFFFFF",
                  border: `1px solid ${INK}`,
                  borderRadius: 999,
                  cursor: "pointer",
                }}
              >
                View receipt
              </div>
            )}
            <div
              onClick={onReset}
              style={{
                fontFamily: SANS,
                fontSize: 14,
                fontWeight: 500,
                padding: isMobile ? "12px 22px" : "13px 26px",
                // When this is the only action (no receipt button next to
                // it), give it the filled/primary treatment instead of the
                // outlined secondary one — a lone outlined pill reads as
                // less confident than a single filled CTA.
                background: onReceipt ? "transparent" : INK,
                color: onReceipt ? INK : "#FFFFFF",
                border: `1px solid ${INK}`,
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              {resetLabel}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ==================================================================== shell */

export default function EditorialForm({
  activeTab,
  onTab,

  netLabel,
  onOpenNetwork,
  tokenIcon,
  chainIcon,
  balanceLabel,

  amount,
  onAmount,
  onPercent,
  percentEnabled = true,
  isOver = false,
  amountError,

  recipient,
  onRecipient,
  recipientError,
  // Testnet-only shortcut; null/omitted on mainnet.
  demoAddress = null,

  note,
  ctaLabel,
  ctaEnabled,
  onSubmit,
  onRequestTransfer,

  children,
}) {
  const showReceiver = activeTab === "transfer";
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        width: "100%",
        maxWidth: isMobile ? "100%" : 600,
        boxSizing: "border-box",
        padding: isMobile ? "0 16px" : 0,
        margin: isMobile ? "32px auto 0" : "74px auto 0",
        paddingBottom: isMobile ? 48 : 80,
      }}
    >
      <style>{"@keyframes st-spin{to{transform:rotate(360deg)}}"}</style>

      <Tabs activeTab={activeTab} onTab={onTab} />

      {children ? (
        children
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <AmountCard
            netLabel={netLabel}
            onOpenNetwork={onOpenNetwork}
            tokenIcon={tokenIcon}
            chainIcon={chainIcon}
            balanceLabel={balanceLabel}
            amount={amount}
            onAmount={onAmount}
            onPercent={onPercent}
            percentEnabled={percentEnabled}
            isOver={isOver}
          />

          {showReceiver && (
            <ReceiverRow
              value={recipient}
              onChange={onRecipient}
              demoAddress={demoAddress}
              error={recipientError}
            />
          )}

          {(note || amountError) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                padding: "2px 2px 0",
              }}
            >
              {amountError ? (
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: 10.5,
                    letterSpacing: "0.1em",
                    color: DANGER,
                  }}
                >
                  {amountError}
                </div>
              ) : null}
              {note || null}
            </div>
          )}

          <Cta label={ctaLabel} enabled={ctaEnabled} onClick={onSubmit} />
          {showReceiver && onRequestTransfer && (
            <button
              type="button"
              onClick={onRequestTransfer}
              style={{
                alignSelf: "center",
                background: "none",
                border: "none",
                color: LINK,
                cursor: "pointer",
                fontFamily: MONO,
                fontSize: 14,
                letterSpacing: "0.01em",
                padding: "2px 0",
              }}
            >
              Request a Payment
            </button>
          )}
        </div>
      )}
    </div>
  );
}
